/**
 * Source module for the Pax Studios web client.
 */
/**
 * Provides browser-side API helpers for auth, profile, billing, and issues.
 */
const API_BASE = process.env.REACT_APP_API_BASE_URL;

//LogTo authentication functions
/**
 * Redirects the browser to the backend sign-in endpoint.
 */
export function signIn() {
  window.location.href = `${API_BASE}/logto/sign-in`;
}
/**
 * Redirects the browser to the backend sign-out endpoint.
 */
export function signOut() {
  window.location.href = `${API_BASE}/api/auth/sign-out`;
}
/**
 * Retrieves the active authenticated session for the current user.
 */
export async function getSession() {
  const res = await fetch(`${API_BASE}/api/auth/session`, {
    credentials: 'include',
  });

  return readJson(res);
}

//user profile functions
/**
 * Fetches user profile data by email payload.
 */
export async function getUserProfileByEmail(payload) {
  const res = await fetch(`${API_BASE}/api/userProfile/get-user-profile`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  return readJson(res);
}
/**
 * Updates a user's alias value.
 */
export async function updateAlias(payload) {
  const res = await fetch(`${API_BASE}/api/userProfile/update-user-alias`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  return readJson(res);
}
/**
 * Updates a user's selected avatar URL.
 */
export async function updateAvatarUrl(payload) {
  const res = await fetch(`${API_BASE}/api/userProfile/update-user-avatar-url`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  return readJson(res);
}
/**
 * Loads avatar ownership options for the current user.
 */
export async function getAvatarOptions() {
  const res = await fetch(`${API_BASE}/api/userProfile/avatar-options`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return readJson(res);
}

//paypal functions
/**
 * Finalizes a PayPal subscription after approval.
 */
export async function completePayPalSubscription(payload) {
  const res = await fetch(`${API_BASE}/api/paypal/subscription/complete`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  return readJson(res);
}
/**
 * Cancels the current PayPal subscription.
 */
export async function cancelPayPalSubscription() {
  const base = process.env.REACT_APP_API_BASE_URL;
  if (!base || String(base).trim() === "") {
    throw new Error("Missing REACT_APP_API_BASE_URL. Set it to your API base URL (no trailing slash).");
  }

  let res;
  try {
    res = await fetch(`${base}/api/paypal/subscription/cancel`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    });
  } catch (e) {
    const name = e?.name;
    const msg = String(e?.message || e);
    if (name === "TypeError" || /failed to fetch/i.test(msg)) {
      throw new Error(
        "Could not reach the API. Check REACT_APP_API_BASE_URL, that the server is running, and that CORS allows this site (server FRONTEND_ORIGIN must match how you open the app, including http vs https and the hostname)."
      );
    }
    throw e;
  }

  return readJson(res);
}

//issue reporting functions
/**
 * Submits a user-authenticated issue report payload.
 */
export async function submitIssueReport(payload) {
  const res = await fetch(`${API_BASE}/api/issueReport`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  return readJson(res);
}

//utility functions
/**
 * Parses response JSON and throws readable request errors.
 */
async function readJson(res) {
  const data = await res.json().catch(() => null);

  if (!res.ok) {
    throw new Error(data?.error || `Request failed with status ${res.status}`);
  }

  return data;
}